import React, { useMemo, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import {
  CalendarDays,
  MapPin,
  Trophy,
  Users,
  ChevronLeft,
  ClipboardList,
  X,
  Save,
  Check,
} from "lucide-react";

type Match = {
  id: string;
  title: string;
  opponent?: string;
  startAt: string;
  location?: string;
  notes?: string;
  coaches: string[];
};

type TeamPlayer = {
  id: string;
  name: string;
  number?: number;
  position?: string;
};

function cx(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Mock aligné avec TeamCalendar */
function useMockMatches(teamId?: string) {
  const now = new Date();

  const matches: Match[] = [
    {
      id: "e1", // ⚠️ même ID que TeamCalendar
      title: "Match championnat",
      opponent: "St-Chamond",
      startAt: new Date(now.getFullYear(), now.getMonth(), now.getDate() + 2, 18, 0).toISOString(),
      location: "Gymnase EFFR",
      notes: "RDV 16h00 – warm-up 16h20",
      coaches: ["Cyp", "Robin"],
    },
    {
      id: "e4",
      title: "Match amical",
      opponent: "Roanne",
      startAt: new Date(now.getFullYear(), now.getMonth(), now.getDate() + 10, 20, 0).toISOString(),
      location: "Extérieur",
      coaches: ["Cyp"],
    },
  ];

  return { matches };
}

/** Mock players */
function useMockTeamPlayers(teamId?: string) {
  const players: TeamPlayer[] = [
    { id: "p1", name: "Léna", number: 4, position: "PG" },
    { id: "p2", name: "Manon", number: 7, position: "SG" },
    { id: "p3", name: "Inès", number: 12, position: "SF" },
    { id: "p4", name: "Camille", number: 8, position: "PF" },
    { id: "p5", name: "Sarah", number: 15, position: "C" },
    { id: "p6", name: "Julie", number: 10, position: "G" },
  ];

  return { players };
}

export default function TeamMatchDetails() {
  const { teamId, matchId } = useParams<{
    clubId: string;
    teamId: string;
    matchId: string;
  }>();

  const { matches } = useMockMatches(teamId);
  const { players } = useMockTeamPlayers(teamId);

  const match = useMemo(
    () => matches.find((m) => m.id === matchId) ?? null,
    [matches, matchId]
  );

  /** =========================
   *  Convocation (mock local)
   *  - invitedIds: liste des players convoqués
   *  - modal: openConvocation
   *  ========================= */
  const [openConvocation, setOpenConvocation] = useState(false);
  const [invitedIds, setInvitedIds] = useState<string[]>(["p1", "p2", "p3"]); // exemple
  const [draftInvitedIds, setDraftInvitedIds] = useState<string[]>(invitedIds);

  const invitedPlayers = useMemo(() => {
    const set = new Set(invitedIds);
    return players.filter((p) => set.has(p.id));
  }, [players, invitedIds]);

  function openConvocationModal() {
    setDraftInvitedIds(invitedIds); // reset draft
    setOpenConvocation(true);
  }

  function toggleDraft(id: string) {
    setDraftInvitedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function saveConvocation() {
    // Mock: on sauvegarde en state local
    setInvitedIds(draftInvitedIds);
    setOpenConvocation(false);

    // Plus tard: supabase upsert dans une table match_callups
    // match_id + player_id
  }

  if (!match) {
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Match introuvable.
        </div>
        <NavLink
          to="../calendar"
          className="mt-3 inline-flex items-center gap-2 text-sm hover:underline"
        >
          <ChevronLeft className="h-4 w-4" />
          Retour calendrier
        </NavLink>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <NavLink
        to="../calendar"
        className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 hover:underline"
      >
        <ChevronLeft className="h-4 w-4" />
        Retour au calendrier
      </NavLink>

      {/* Header + CTA convocation */}
      <div className="flex flex-col items-center text-center gap-3 sm:flex-row sm:items-center sm:justify-between sm:text-left">
        <div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Détails match</div>
          <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">
            {match.title}
            {match.opponent && (
              <span className="text-gray-500 dark:text-gray-400 font-normal">
                {" "}
                • vs {match.opponent}
              </span>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={openConvocationModal}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-900 text-white hover:bg-black
                     dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 text-sm"
        >
          <ClipboardList className="h-4 w-4" />
          Convocation
        </button>
      </div>

      {/* Infos match */}
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-5">
        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 bg-gray-50 dark:bg-gray-950">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
              <CalendarDays className="h-4 w-4" />
              Date & heure
            </div>
            <div className="mt-2 text-sm text-gray-700 dark:text-gray-200">
              {formatDateTime(match.startAt)}
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 bg-gray-50 dark:bg-gray-950">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
              <MapPin className="h-4 w-4" />
              Lieu
            </div>
            <div className="mt-2 text-sm text-gray-700 dark:text-gray-200">
              {match.location ?? "—"}
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 bg-gray-50 dark:bg-gray-950">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
              <Users className="h-4 w-4" />
              Coachs
            </div>
            <div className="mt-2 text-sm text-gray-700 dark:text-gray-200">
              {match.coaches.join(", ")}
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 bg-gray-50 dark:bg-gray-950">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
              <Trophy className="h-4 w-4" />
              Type
            </div>
            <div className="mt-2 text-sm text-gray-700 dark:text-gray-200">
              Match
            </div>
          </div>

          {match.notes && (
            <div className="md:col-span-2 rounded-xl border border-gray-200 dark:border-gray-800 p-4 bg-gray-50 dark:bg-gray-950">
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Notes</div>
              <div className="mt-2 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">
                {match.notes}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Résumé convocation */}
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Joueurs convoqués
          </div>
          <button
            type="button"
            onClick={openConvocationModal}
            className="text-sm hover:underline text-gray-700 dark:text-gray-200"
          >
            Modifier
          </button>
        </div>

        {invitedPlayers.length === 0 ? (
          <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            Aucun joueur convoqué pour le moment.
          </div>
        ) : (
          <div className="mt-3 flex flex-wrap gap-2">
            {invitedPlayers.map((p) => (
              <span
                key={p.id}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full
                           bg-gray-100 text-gray-900 border border-gray-200
                           dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 text-sm"
              >
                <Check className="h-4 w-4" />
                {p.number != null ? `${p.number} · ` : ""}{p.name}
              </span>
            ))}
          </div>
        )}

        <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          (Mock) Plus tard : on enregistre en base et on peut envoyer une notif.
        </div>
      </div>

      {/* Modal convocation */}
      {openConvocation && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpenConvocation(false)} />
          <div className="absolute inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center p-3">
            <div className="w-full sm:max-w-2xl rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-gray-900 dark:text-gray-100">
                  Convocation — choisir les joueurs
                </div>
                <button
                  type="button"
                  onClick={() => setOpenConvocation(false)}
                  className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:underline"
                >
                  <X className="h-4 w-4" />
                  Fermer
                </button>
              </div>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {players.map((p) => {
                  const checked = draftInvitedIds.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => toggleDraft(p.id)}
                      className={cx(
                        "flex items-center justify-between rounded-xl border p-3 text-left",
                        checked
                          ? "border-orange-500 bg-orange-50 dark:bg-orange-900/10"
                          : "border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
                      )}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {p.number != null ? `${p.number} · ` : ""}{p.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {p.position ?? "—"}
                        </div>
                      </div>

                      <div
                        className={cx(
                          "h-5 w-5 rounded-md border flex items-center justify-center",
                          checked
                            ? "bg-orange-600 border-orange-600 text-white"
                            : "border-gray-300 dark:border-gray-700"
                        )}
                      >
                        {checked ? <Check className="h-4 w-4" /> : null}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setOpenConvocation(false)}
                  className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-800
                             bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100
                             hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={saveConvocation}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm
                             bg-gray-900 text-white hover:bg-black
                             dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
                >
                  <Save className="h-4 w-4" />
                  Enregistrer
                </button>
              </div>

              <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                (Mock) Ensuite : table match_callups + RLS + notif.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
